import { Injectable } from '@angular/core';
import { ApiBaseService } from '@core/services/api-base.service';
import { Mapper } from '@core/services/mapper';
import { Observable } from 'rxjs';
import { ApiConstant } from '@shared/constants';
import { map } from 'rxjs/operators';
import { urlPattern } from '@shared/validators/form-validators';
import { RequestLog } from '@shared-features/models/request-log.model';
import { ShipmentIntegration } from '@features/shipments/models/shipment-integration.model';
import { filterNullEntity } from '@shared/utils/filter-null-entity.util';
import { ShipmentCreationTemplateTypes } from '@shared/enums/shipment-creation-template-types.enum';
import { environment } from '@env/environment';

@Injectable({ providedIn: 'root' })
export class IntegrationsApiService {
  constructor(private baseAPI: ApiBaseService, private mapper: Mapper) {}

  getShipmentIntegrations(shipmentId: number): Observable<ShipmentIntegration> {
    return this.baseAPI
      .get(`${ApiConstant.GET_SHIPMENT_INTEGRATIONS}/${shipmentId}`)
      .pipe(map((res) => this.mapper.fromJson(ShipmentIntegration, res.data)));
  }

  getB2cShipmentIntegrations(shipmentId: number): Observable<ShipmentIntegration> {
    return this.baseAPI
      .get(`${ApiConstant.GET_B2C_SHIPMENT_INTEGRATIONS}/${shipmentId}`)
      .pipe(map((res) => this.mapper.fromJson(ShipmentIntegration, res.data)));
  }

  getFleetDynamicIntegrations(params: {
    fleetId: number;
    shipmentId: number;
  }): Observable<ShipmentIntegration> {
    return this.baseAPI
      .get(ApiConstant.GET_FLEET_DYNAMIC_INTEGRATION, {
        params: filterNullEntity<typeof params>(params),
      })
      .pipe(map((res) => this.mapper.fromJson(ShipmentIntegration, res.data)));
  }

  integrateShipment(payload: { shipmentId: number; type: string }): Observable<any> {
    const headers = this.baseAPI.getHeaders().append('appKey', environment.appApiKey);
    return this.baseAPI.post(ApiConstant.INTEGRATE_SHIPMENT, { ...payload, data: {} }, { headers });
  }

  fleetIntegrateShipment(payload: { shipmentId: number; type: string }): Observable<any> {
    return this.baseAPI.post(ApiConstant.FLEET_INTEGRATE_SHIPMENT, payload);
  }

  integrateWithBayan(
    shipmentId: number,
    creationTemplateType: ShipmentCreationTemplateTypes
  ): Observable<any> {
    return this.baseAPI.get(ApiConstant.BAYAN_INTEGRATE_TRIP + '/' + shipmentId, {
      params: { creationTemplateType: creationTemplateType },
    });
  }

  cancelBayanWaybill(shipmentId: number, waybillId?: number): Observable<any> {
    const payload = filterNullEntity({
      waybillId: waybillId,
    });
    return this.baseAPI.put(ApiConstant.BAYAN_CANCEL_WAYBILL + '/' + shipmentId, payload);
  }

  integrateWithCement(shipmentId: number): Observable<string> {
    return this.baseAPI.post(ApiConstant.WCC_INTEGRATE_WITH_CEMENT, { shipmentId });
  }

  downloadBayanPDF(shipmentId: number): Observable<string> {
    return this.baseAPI.get(ApiConstant.PRINT_BAYAN_TRIP + '/' + shipmentId).pipe(
      map((res) => {
        if (res && urlPattern.test(res.data)) {
          return res.data;
        }
        const array = new Uint8Array(res.data);
        const blob = new Blob([array], { type: 'application/pdf' });
        const file = new File([blob], `bayan-${shipmentId}.pdf`, {
          type: blob.type,
          lastModified: Date.now(),
        });
        return window.URL.createObjectURL(file);
      })
    );
  }

  getCementOrderLogs(id: number): Observable<RequestLog[]> {
    return this.baseAPI.get(ApiConstant.WCC_GET_ORDER_LOGS + '/' + id).pipe(
      map((res) => {
        return this.mapper.fromJson(RequestLog, res.data);
      })
    );
  }
}
